import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { updateStoredLanguage } from '@/shared/api/user-preferences.api';
import { loadLocale } from '@/shared/i18n/config';
import { LANGUAGE_DEPENDENT_QUERY_PREFIXES } from '@/shared/i18n/language-dependent-queries';
import { isSupportedLocale } from '@/shared/i18n/locale-resolution';
import { getSupportedLocales, type SupportedLocale } from '@/shared/i18n/locales';
import { clearCachedLocale, writeCachedLocale } from '@/shared/i18n/locale-cache';
import { queryClient } from '@/lib/config';
import { logger } from '@/lib/utils/logger';

export interface UseLanguageResult {
  current: string;
  options: SupportedLocale[];
  /** No-ops on a locale whose catalogue will not load, rather than showing raw keys. */
  setLanguage: (code: string) => Promise<void>;
}

/**
 * Deliberately stateless: i18n is already the state and re-renders every mounted
 * `useTranslation` on change, so a store beside it would be a second copy to
 * keep in step.
 */
export function useLanguage(): UseLanguageResult {
  const { i18n } = useTranslation();
  const options = getSupportedLocales();

  const setLanguage = useCallback(
    async (code: string) => {
      // Checked before fetching, because `loadLocale` resolves quietly on a code
      // it does not know: without this an unsupported value would switch the app
      // to a language that has no catalogue, and then persist it.
      if (!isSupportedLocale(code)) return;

      // The catalogue is fetched on demand, so this is a real network wait the
      // first time a locale is picked. Bail rather than switching, or the
      // interface renders raw keys.
      try {
        await loadLocale(code);
      } catch (error) {
        logger.error('Failed to load the language catalogue:', error);
        return;
      }

      // Switch before persisting, so the interface does not also wait on the
      // preference write to show the language it already has.
      await i18n.changeLanguage(code);
      writeCachedLocale(code);
      void Promise.all(
        LANGUAGE_DEPENDENT_QUERY_PREFIXES.map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      );

      try {
        await updateStoredLanguage(code);
      } catch (error) {
        // Asymmetric on purpose: the session keeps the chosen language because
        // yanking the interface back is worse than a choice that did not
        // persist, but the cache goes, or the next boot would resolve a value
        // the record never accepted.
        clearCachedLocale();
        logger.error('Failed to persist language preference:', error);
      }
    },
    [i18n],
  );

  return { current: i18n.language, options, setLanguage };
}
